
import Vue, { PropType } from 'vue';
import * as tus from 'tus-js-client';
import { GameEntry, GameMaterials, GameSavePayload } from './types';
import { getToken } from '@/auth';
import { apiFetch, refreshAccessToken } from '@/services/apiClient';

type DocField = 'gdd' | 'cad' | 'marketing' | 'latestArt' | 'approvedMaterials';

const FIELD_NAMES: Record<DocField, string> = {
  gdd: 'Game Design Document',
  cad: 'Concept Approval Document',
  marketing: 'Marketing Materials',
  latestArt: 'Latest Art',
  approvedMaterials: 'Approved Materials',
};

export default Vue.extend({
  name: 'GameMaterialModal',

  props: {
    game: {
      type: Object as PropType<GameEntry>,
      required: true,
    },
    materials: {
      type: Object as PropType<GameMaterials>,
      required: true,
    },
    saving: {
      type: Boolean,
      default: false,
    },
    deleting: {
      type: Boolean,
      default: false,
    },
    brandOptions: {
      type: Array as PropType<Array<{ id: string; name: string; filePath: string; brandId: string }>>,
      default: () => [],
    },
    gameData: {
      type: Object as PropType<Record<string, unknown>>,
      default: () => ({}),
    },
  },

  async mounted() {
    try {
      const res = await apiFetch(`/api/games/${this.game.gameId}/materials`);
      if (!res.ok) return;
      const records: Array<{ filename: string; cdn_url: string }> =
        await res.json();
      const urlToName: Record<string, string> = {};
      for (const r of records) {
        urlToName[r.cdn_url] = r.filename;
      }
      const fields: DocField[] = [
        'gdd',
        'cad',
        'marketing',
        'latestArt',
        'approvedMaterials',
      ];
      for (const field of fields) {
        for (const item of this.localMaterials[field]) {
          if (item.url && urlToName[item.url]) {
            this.$set(item, 'name', urlToName[item.url]);
          }
        }
      }
    } catch {
      // non-critical — display names fall back to seeded values
    }
  },

  computed: {
    thumbSrc(): string {
      return this.resolveThumbSrc(this.localThumbnail);
    },
    bannerSrc(): string {
      return this.resolveThumbSrc(this.localBanner);
    },
    /**
     * True when a value is set but cannot be previewed — either the bundled
     * asset does not exist (resolveThumbSrc returned '') or the <img> failed to
     * load. Keeps the slot out of a dead state: it still renders its actions.
     */
    thumbBroken(): boolean {
      return !!this.localThumbnail && (!this.thumbSrc || this.thumbLoadFailed);
    },
    bannerBroken(): boolean {
      return !!this.localBanner && (!this.bannerSrc || this.bannerLoadFailed);
    },
    imageUploading(): boolean {
      return this.thumbnailUploading || this.bannerUploading;
    },
    selectedCategoryName(): string {
      const opt = (this.brandOptions as Array<{ id: string; name: string }>).find(
        (b) => b.id === this.localCategory
      );
      return opt?.name || this.localCategory;
    },

    safeDemoLink(): string {
      const url = this.localMaterials.demoLink || '';
      return url.startsWith('http://') || url.startsWith('https://')
        ? url
        : '#';
    },
    isUploading(): boolean {
      return Object.values(this.activeUploads).some(
        (arr) => (arr as any[]).length > 0
      );
    },
    isMetaJsonValid(): boolean {
      try {
        JSON.parse(this.localMetaJson);
        return true;
      } catch {
        return false;
      }
    },
  },

  data() {
    return {
      localName: this.game.name,
      localNameEditing: false,
      localCategory: this.game.categoryName,
      categoryMenuOpen: false,
      imagesOpen: true,
      localThumbnail: this.game.thumbnail,
      thumbnailUploading: false,
      thumbLoadFailed: false,
      localBanner: this.game.banner,
      bannerUploading: false,
      bannerLoadFailed: false,
      pendingDemoLink: '',
      pendingGddLink: '',
      pendingCadLink: '',
      pendingMarketingLink: '',
      pendingLatestArtLink: '',
      pendingApprovedLink: '',
      localMaterials: {
        demoLink: this.materials.demoLink,
        gdd: this.materials.gdd.slice(-1).map((f) => ({ ...f })),
        cad: this.materials.cad.slice(-1).map((f) => ({ ...f })),
        marketing: this.materials.marketing.slice(-1).map((f) => ({ ...f })),
        latestArt: this.materials.latestArt.slice(-1).map((f) => ({ ...f })),
        approvedMaterials: this.materials.approvedMaterials.slice(-1).map((f) => ({
          ...f,
        })),
      } as GameMaterials,
      showDeleteConfirm: false,
      localMetaJson: JSON.stringify(this.gameData, null, 2) || '{}',
      isDraggingGdd: false,
      isDraggingCad: false,
      isDraggingMarketing: false,
      isDraggingLatestArt: false,
      isDraggingApproved: false,
      activeUploads: {
        gdd: [] as Array<{ id: number; name: string; progress: number }>,
        cad: [] as Array<{ id: number; name: string; progress: number }>,
        marketing: [] as Array<{ id: number; name: string; progress: number }>,
        latestArt: [] as Array<{ id: number; name: string; progress: number }>,
        approvedMaterials: [] as Array<{
          id: number;
          name: string;
          progress: number;
        }>,
      } as Record<
        DocField,
        Array<{ id: number; name: string; progress: number }>
      >,
      nextUploadId: 0,
      copiedUrl: null as string | null,
    };
  },

  watch: {
    // A new value deserves a fresh attempt at previewing it.
    localThumbnail() {
      this.thumbLoadFailed = false;
    },
    localBanner() {
      this.bannerLoadFailed = false;
    },
  },

  methods: {
    resolveThumbSrc(path: string): string {
      if (!path) return '';
      if (path.startsWith('http://') || path.startsWith('https://')) return path;
      try {
        return require(`@/assets/${path}`);
      } catch {
        return '';
      }
    },
    triggerThumbUpload() {
      (this.$refs.thumbInput as HTMLInputElement).click();
    },

    /**
     * Uploads the picked file and returns its CDN URL, or null if the user
     * picked nothing or the upload failed. Shared by both image slots.
     */
    async uploadImage(event: Event, kind: 'thumbnail' | 'banner'): Promise<string | null> {
      const input = event.target as HTMLInputElement;
      const file = input.files?.[0];
      if (!file) return null;
      // Reset so re-picking the same file fires 'change' again.
      input.value = '';
      try {
        const fd = new FormData();
        fd.append('file', file);
        const res = await apiFetch(`/api/games/${this.game.gameId}/${kind}`, {
          method: 'POST',
          body: fd,
        });
        if (!res.ok) throw new Error(`Upload failed (${res.status})`);
        const { url } = await res.json();
        return url;
      } catch (e) {
        (this as any).$toast?.error(`Failed to upload ${kind}. Please try again.`);
        return null;
      }
    },

    async onThumbSelected(event: Event) {
      this.thumbnailUploading = true;
      try {
        const url = await this.uploadImage(event, 'thumbnail');
        if (url) this.localThumbnail = url;
      } finally {
        this.thumbnailUploading = false;
      }
    },

    triggerBannerUpload() {
      (this.$refs.bannerInput as HTMLInputElement).click();
    },

    async onBannerSelected(event: Event) {
      this.bannerUploading = true;
      try {
        const url = await this.uploadImage(event, 'banner');
        if (url) this.localBanner = url;
      } finally {
        this.bannerUploading = false;
      }
    },

    async copyUrl(url: string) {
      await navigator.clipboard.writeText(url);
      this.copiedUrl = url;
      (this as any).$toast?.success('Copied to clipboard!');
      setTimeout(() => {
        this.copiedUrl = null;
      }, 2000);
    },

    isValidUrl(url: string): boolean {
      const trimmed = url.trim();
      return trimmed.startsWith('http://') || trimmed.startsWith('https://');
    },

    onDragLeave(
      event: DragEvent,
      field:
        | 'isDraggingGdd'
        | 'isDraggingCad'
        | 'isDraggingMarketing'
        | 'isDraggingLatestArt'
        | 'isDraggingApproved'
    ) {
      const zone = event.currentTarget as HTMLElement;
      if (!zone.contains(event.relatedTarget as Node)) {
        (this as any)[field] = false;
      }
    },

    sectionComplete(field: DocField): boolean {
      return this.localMaterials[field].length > 0;
    },

    isSectionEmpty(field: DocField): boolean {
      return this.localMaterials[field].length === 0 && this.activeUploads[field].length === 0;
    },

    removeFile(field: DocField, idx: number) {
      this.localMaterials[field].splice(idx, 1);
    },

    commitLink(field: DocField, linkValue: string) {
      const url = linkValue.trim();
      if (!this.isValidUrl(url)) return;
      this.localMaterials[field].splice(0, this.localMaterials[field].length, {
        name: FIELD_NAMES[field],
        type: 'LINK',
        url,
      });
    },

    uploadFile(field: DocField, file: File) {
      const uploadId = this.nextUploadId++;
      this.activeUploads[field].push({
        id: uploadId,
        name: file.name,
        progress: 0,
      });

      // The access token is short-lived (15m) and large uploads can outlast it, so the
      // header is set fresh per-request rather than once at start, with a refresh-and-retry
      // on 401 — mirrors the fetch-based refresh flow in apiClient.ts.
      let needsTokenRefresh = false;
      const upload = new tus.Upload(file, {
        endpoint: '/api/games/materials/tus',
        storeFingerprintForResuming: true,
        retryDelays: [0, 3000, 5000, 10000, 20000],
        chunkSize: 10 * 1024 * 1024,
        onBeforeRequest: async (req) => {
          if (needsTokenRefresh) {
            needsTokenRefresh = false;
            await refreshAccessToken();
          }
          req.setHeader('Authorization', `Bearer ${getToken() ?? ''}`);
        },
        onShouldRetry: (error, retryAttempt, options) => {
          if (error.originalResponse?.getStatus() === 401) {
            needsTokenRefresh = true;
            return retryAttempt < (options.retryDelays?.length ?? 0);
          }
          return tus.defaultOptions.onShouldRetry?.(error, retryAttempt, options) ?? false;
        },
        metadata: {
          gameId: String(this.game.gameId),
          filename: file.name,
        },
        onProgress: (bytesUploaded, bytesTotal) => {
          const entry = this.activeUploads[field].find(
            (u) => u.id === uploadId
          );
          if (entry)
            entry.progress = Math.round((bytesUploaded / bytesTotal) * 100);
        },
        onSuccess: ({ lastResponse }) => {
          const cdnUrl = lastResponse.getHeader('Upload-CDN-URL');
          const ext = this.getExtension(file.name);
          this.localMaterials[field].splice(0, this.localMaterials[field].length, {
            name: file.name,
            type: ext,
            url: cdnUrl,
          });
          const idx = this.activeUploads[field].findIndex(
            (u) => u.id === uploadId
          );
          if (idx >= 0) this.activeUploads[field].splice(idx, 1);
        },
        onError: (e) => {
          console.error('Upload failed', e);
          (this as any).$toast?.error(
            'Failed to upload file. Please try again.'
          );
          const idx = this.activeUploads[field].findIndex(
            (u) => u.id === uploadId
          );
          if (idx >= 0) this.activeUploads[field].splice(idx, 1);
        },
      });

      upload.start();
    },

    onDropGdd(event: DragEvent) {
      this.isDraggingGdd = false;
      const file = event.dataTransfer?.files[0];
      if (!file) return;
      this.uploadFile('gdd', file);
    },

    onDropCad(event: DragEvent) {
      this.isDraggingCad = false;
      const file = event.dataTransfer?.files[0];
      if (!file) return;
      this.uploadFile('cad', file);
    },

    onDropMarketing(event: DragEvent) {
      this.isDraggingMarketing = false;
      const file = event.dataTransfer?.files[0];
      if (!file) return;
      this.uploadFile('marketing', file);
    },

    onDropLatestArt(event: DragEvent) {
      this.isDraggingLatestArt = false;
      const file = event.dataTransfer?.files[0];
      if (!file) return;
      this.uploadFile('latestArt', file);
    },

    onDropApproved(event: DragEvent) {
      this.isDraggingApproved = false;
      const file = event.dataTransfer?.files[0];
      if (!file) return;
      this.uploadFile('approvedMaterials', file);
    },

    onGddFile(event: Event) {
      const input = event.target as HTMLInputElement;
      const file = input.files?.[0];
      if (!file) return;
      input.value = '';
      this.uploadFile('gdd', file);
    },

    onCadFile(event: Event) {
      const input = event.target as HTMLInputElement;
      const file = input.files?.[0];
      if (!file) return;
      input.value = '';
      this.uploadFile('cad', file);
    },

    onMarketingFile(event: Event) {
      const input = event.target as HTMLInputElement;
      const file = input.files?.[0];
      if (!file) return;
      input.value = '';
      this.uploadFile('marketing', file);
    },

    onLatestArtFile(event: Event) {
      const input = event.target as HTMLInputElement;
      const file = input.files?.[0];
      if (!file) return;
      input.value = '';
      this.uploadFile('latestArt', file);
    },

    onApprovedFile(event: Event) {
      const input = event.target as HTMLInputElement;
      const file = input.files?.[0];
      if (!file) return;
      input.value = '';
      this.uploadFile('approvedMaterials', file);
    },

    getExtension(filename: string): string {
      const parts = filename.split('.');
      return parts.length > 1 ? parts[parts.length - 1].toUpperCase() : 'FILE';
    },

    confirmDelete() {
      this.showDeleteConfirm = false;
      this.$emit('delete', this.game.gameId);
    },

    save() {
      // Confirm name edit if still open
      this.localNameEditing = false;

      // Auto-commit any pending links that were typed but not explicitly added
      if (this.isValidUrl(this.pendingDemoLink)) {
        this.localMaterials.demoLink = this.pendingDemoLink.trim();
        this.pendingDemoLink = '';
      }
      if (this.isValidUrl(this.pendingGddLink) && this.isSectionEmpty('gdd')) {
        this.commitLink('gdd', this.pendingGddLink);
        this.pendingGddLink = '';
      }
      if (this.isValidUrl(this.pendingCadLink) && this.isSectionEmpty('cad')) {
        this.commitLink('cad', this.pendingCadLink);
        this.pendingCadLink = '';
      }
      if (this.isValidUrl(this.pendingMarketingLink) && this.isSectionEmpty('marketing')) {
        this.commitLink('marketing', this.pendingMarketingLink);
        this.pendingMarketingLink = '';
      }
      if (this.isValidUrl(this.pendingLatestArtLink) && this.isSectionEmpty('latestArt')) {
        this.commitLink('latestArt', this.pendingLatestArtLink);
        this.pendingLatestArtLink = '';
      }
      if (this.isValidUrl(this.pendingApprovedLink) && this.isSectionEmpty('approvedMaterials')) {
        this.commitLink('approvedMaterials', this.pendingApprovedLink);
        this.pendingApprovedLink = '';
      }

      const selectedCat = (this.brandOptions as Array<{ id: string; name: string; filePath: string; brandId: string }>)
        .find((b) => b.id === this.localCategory);

      let gameMeta: Record<string, unknown> = {};
      try { gameMeta = JSON.parse(this.localMetaJson); } catch { /* blocked by isMetaJsonValid */ }

      this.$emit('save', {
        materials: { ...this.localMaterials },
        metadata: {
          name: this.localName.trim() || this.game.name,
          categoryName: this.localCategory,
          filePath: selectedCat?.filePath || this.game.filePath,
          brandId: selectedCat?.brandId || this.game.brandId,
          thumbnail: this.localThumbnail,
          banner: this.localBanner,
          gameMeta,
        },
      } as GameSavePayload);
    },
  },
});
